import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Button from '../ui/Button';
import Card from '../ui/Card';
import { Download, RefreshCw, Search, Plus, Copy, Check } from 'lucide-react';

const EnrichmentJobsTable = ({ onCreateNew, settings }) => {
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/api/jobs/enrichment`, {
        timeout: 15000,
        params: { limit: 50, skip: 0 }
      });
      setJobs(response.data);
    } catch (err) {
      console.error('Error fetching enrichment jobs:', err);
      setError('Failed to load enrichment jobs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  // Filter jobs by search
  const filteredJobs = useMemo(() => {
    if (!search.trim()) return jobs;
    const q = search.toLowerCase();
    return jobs.filter(j => (j.source_file_name || '').toLowerCase().includes(q));
  }, [jobs, search]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const utcDateString = dateString.includes('Z') ? dateString : dateString + 'Z';
      const date = new Date(utcDateString);
      const timezone = settings?.timezone || 'Asia/Kolkata';
      return date.toLocaleString('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'csv_ready': { bg: 'var(--color-warning-bg)', color: 'var(--color-warning-hover)', label: 'CSV Ready' },
      'submitted_to_brightdata': { bg: 'var(--color-warning-bg)', color: 'var(--color-warning-hover)', label: 'Processing' },
      'brightdata_complete': { bg: 'var(--color-success-bg)', color: 'var(--color-success-fg)', label: 'BD Complete' },
      'extracting': { bg: 'var(--color-warning-bg)', color: 'var(--color-warning-hover)', label: 'Extracting' },
      'completed': { bg: 'var(--color-success-bg)', color: 'var(--color-success-fg)', label: 'completed' },
      'failed': { bg: 'var(--color-danger-bg)', color: 'var(--color-danger-hover)', label: 'failed' },
    };

    const config = statusConfig[status] || { bg: 'var(--color-bg-inset)', color: 'var(--color-text-muted)', label: status };

    return (
      <span style={{
        padding: '2px 8px',
        borderRadius: 12,
        fontSize: 12,
        backgroundColor: config.bg,
        color: config.color,
        border: '1px solid var(--color-border)'
      }}>
        {config.label}
      </span>
    );
  };

  return (
    <Card>
      <Card.Header>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Card.Title>AI Enrichment Jobs</Card.Title>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ position: 'relative' }}>
              <input
                placeholder="Search by file name"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  padding: '8px 28px 8px 10px',
                  border: '1px solid var(--color-border)',
                  borderRadius: 8,
                  fontSize: 14
                }}
              />
              <Search size={14} style={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--color-text-disabled)'
              }} />
            </div>
            <Button
              variant="secondary"
              onClick={fetchJobs}
              icon={<RefreshCw size={14} />}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              onClick={onCreateNew}
              icon={<Plus size={14} />}
            >
              New Job
            </Button>
          </div>
        </div>
      </Card.Header>
      <Card.Content>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: 'var(--color-text-muted)' }}>
                  Date
                </th>
                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: 'var(--color-text-muted)' }}>
                  File Name
                </th>
                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: 'var(--color-text-muted)' }}>
                  Job ID
                </th>
                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: 'var(--color-text-muted)' }}>
                  Status
                </th>
                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: 'var(--color-text-muted)' }}>
                  Rows
                </th>
                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: 'var(--color-text-muted)' }}>
                  Types
                </th>
                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: 'var(--color-text-muted)' }}>
                  Export Data
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" style={{ padding: 20, textAlign: 'center', color: 'var(--color-text-muted)' }}>
                    Loading...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="7" style={{ padding: 20, textAlign: 'center', color: 'var(--color-danger)' }}>
                    {error}
                  </td>
                </tr>
              ) : filteredJobs.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ padding: 20, textAlign: 'center', color: 'var(--color-text-muted)' }}>
                    {search ? 'No jobs found matching your search' : 'No enrichment jobs yet'}
                  </td>
                </tr>
              ) : (
                filteredJobs.map((job) => (
                  <tr key={job.id} style={{ borderBottom: '1px solid var(--color-bg-inset)' }}>
                    <td style={{ padding: '10px 12px', fontSize: 13, color: 'var(--color-text-secondary)' }}>
                      {formatDate(job.created_at)}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 13, color: 'var(--color-text-primary)', fontWeight: 600 }}>
                      {job.source_file_name || 'N/A'}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 13, color: 'var(--color-text-secondary)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <code style={{
                          fontSize: 11,
                          backgroundColor: 'var(--color-bg-inset)',
                          padding: '2px 6px',
                          borderRadius: 3,
                          fontFamily: 'monospace',
                          color: 'var(--color-text-muted)'
                        }}>
                          {job.id.substring(0, 8)}
                        </code>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(job.id);
                            setCopiedId(job.id);
                            setTimeout(() => setCopiedId(null), 2000);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 2,
                            display: 'flex',
                            alignItems: 'center',
                            color: copiedId === job.id ? 'var(--color-success)' : 'var(--color-text-disabled)',
                            transition: 'color 0.2s'
                          }}
                          title="Copy full Job ID"
                        >
                          {copiedId === job.id ? <Check size={14} /> : <Copy size={14} />}
                        </button>
                      </div>
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 12 }}>
                      {getStatusBadge(job.status)}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 13 }}>
                      {(job.total_rows || 0).toLocaleString()}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 13 }}>
                      {job.total_columns || 0}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      {job.status === 'completed' && job.result_file_url ? (
                        <Button
                          size="sm"
                          onClick={async () => {
                            try {
                              const response = await axios.get(
                                `${API_BASE_URL}/api/jobs/enrichment/${job.id}/download-final`
                              );
                              if (response.data.download_url) {
                                window.open(response.data.download_url, '_blank');
                              }
                            } catch (err) {
                              console.error('Error downloading enriched CSV:', err);
                              alert('Failed to download enriched CSV');
                            }
                          }}
                          icon={<Download size={14} />}
                        >
                          Download
                        </Button>
                      ) : (
                        <span style={{ fontSize: 12, color: 'var(--color-text-disabled)' }}>—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card.Content>
    </Card>
  );
};

export default EnrichmentJobsTable;
